import { logger } from "@/utils/logger";
import { Ability, AbilityBuilder } from "@casl/ability";
import { EMPLOYER_ABILITY_DEFINITION } from "./constants";

// Subject→Action のマップを [Action, Subject] タプルのユニオンに変換するユーティリティ型
type AbilityToTuple<T extends Record<PropertyKey, PropertyKey>> = {
  [K in keyof T]: T[K] extends infer V
    ? V extends any
      ? [V, K]
      : never
    : never;
}[keyof T];

type EmployerAbility = {
  [
    K in keyof typeof EMPLOYER_ABILITY_DEFINITION
  ]: (typeof EMPLOYER_ABILITY_DEFINITION)[K][number];
};

export type Subject = keyof EmployerAbility;

type EmployerAbilityTuple = AbilityToTuple<EmployerAbility>;

export type EmployerAbilityInstance = Ability<EmployerAbilityTuple>;

/**
 * 既知のSubjectかどうかを判定する型ガード
 */
function isKnownSubject(resource: string): resource is Subject {
  return Object.prototype.hasOwnProperty.call(
    EMPLOYER_ABILITY_DEFINITION,
    resource,
  );
}

/**
 * 既知のActionかどうかを判定する型ガード
 */
function isKnownAction(
  subject: Subject,
  action: string,
): action is EmployerAbility[typeof subject] {
  const validActions = EMPLOYER_ABILITY_DEFINITION[
    subject
  ] as readonly string[];
  return validActions.includes(action);
}

/**
 * 個別のAbilityレコード型
 */
export type EmployerAbilityRecord = {
  resource: string;
  action: string;
};

/**
 * AbilityリストからAbilityインスタンスを構築
 */
export function buildEmployerAbility(
  abilities: ReadonlyArray<EmployerAbilityRecord>,
): EmployerAbilityInstance {
  const { can, build } = new AbilityBuilder<EmployerAbilityInstance>(Ability);

  // 取得したアビリティをCASLルールに変換
  for (const ability of abilities) {
    const { resource, action } = ability;

    // 型ガードで既知のresource/actionかチェック
    if (!isKnownSubject(resource)) {
      logger.warn(
        { resource, action },
        "buildEmployerAbility: unknown resource, skipping",
      );
      continue;
    }

    if (!isKnownAction(resource, action)) {
      logger.warn(
        { resource, action },
        "buildEmployerAbility: unknown action for resource, skipping",
      );
      continue;
    }

    can(action, resource);
  }

  return build();
}

// Subjectごとに許可されたActionのみを抽出する
export type SubjectActions<Sub extends Subject> = Extract<
  EmployerAbilityTuple,
  [any, Sub]
>[0];

export const checkEmployerPermission = <Sub extends Subject>(
  ability: EmployerAbilityInstance,
  subject: Sub,
  action: SubjectActions<Sub>,
): boolean => {
  return ability.can(action, subject);
};
